import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaCheckCircle,
  FaSearch,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Apiurl from "../../environment/Apiurl";

const Brand = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [brands, setBrands] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [editBrand, setEditBrand] = useState(null);
  const [deleteBrandId, setDeleteBrandId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch all brands from API
  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${Apiurl}/brand`);
      setBrands(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching brands:", error);
      setError("Failed to fetch brands. Please try again.");
      setLoading(false);
    }
  };

  const [privileges, setPrivileges] = useState(null);

  // Fetch privileges from localStorage
  useEffect(() => {
    const getaccess = localStorage.getItem("role");
    setPrivileges(getaccess);
  }, []);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({
      top: 0,
      behavior: "smooth", // optional for smooth scrolling
    });
  }, []);

  // Handle modal open/close
  const handleCloseAddModal = () => setShowAddModal(false);
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditBrand(null);
  };
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteBrandId(null);
  };

  // Handle adding a brand
  const handleAddBrand = async (e) => {
    e.preventDefault();
    const form = e.target;
    const brandData = {
      brand_name: form.brandName.value,
      logo: form.brandLogo.value,
      status: form.brandStatus.value,
    };

    try {
      const response = await axios.post(`${Apiurl}/brand`, brandData);
      setBrands([...brands, response.data]);
      form.reset();
      handleCloseAddModal();
    } catch (error) {
      console.error("Error adding brand:", error);
      setError("Failed to add brand. Please try again.");
    }
  };

  // Handle editing a brand
  const handleEditBrand = async (e) => {
    e.preventDefault();
    const form = e.target;
    const brandData = {
      brand_name: form.brandName.value,
      logo: form.brandLogo.value,
      status: form.brandStatus.value,
    };

    try {
      const response = await axios.put(
        `${Apiurl}/brand/${editBrand._id}`,
        brandData,
      );
      setBrands(
        brands.map((brand) =>
          brand._id === editBrand._id ? response.data : brand,
        ),
      );
      form.reset();
      handleCloseEditModal();
    } catch (error) {
      console.error("Error updating brand:", error);
      setError("Failed to update brand. Please try again.");
    }
  };

  // Handle delete brand
  const handleDeleteBrand = (id) => {
    setDeleteBrandId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${Apiurl}/brand/${deleteBrandId}`);
      setBrands(brands.filter((brand) => brand._id !== deleteBrandId));
      handleCloseDeleteModal();
    } catch (error) {
      console.error("Error deleting brand:", error);
      setError("Failed to delete brand. Please try again.");
    }
  };

  // Handle pagination
  const filteredBrands = brands.filter(
    (brand) =>
      brand.brand_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (brand.logo &&
        brand.logo.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  const indexOfLastItem =
    itemsPerPage === "All"
      ? filteredBrands.length
      : currentPage * parseInt(itemsPerPage);
  const indexOfFirstItem =
    itemsPerPage === "All" ? 0 : indexOfLastItem - parseInt(itemsPerPage);
  const currentBrands = filteredBrands.slice(indexOfFirstItem, indexOfLastItem);
  const effectiveTotalPages =
    itemsPerPage === "All"
      ? 1
      : Math.ceil(filteredBrands.length / parseInt(itemsPerPage));

  // Generate pagination with ellipsis logic
  const getPageList = () => {
    const pages = [];
    if (effectiveTotalPages <= 7) {
      for (let i = 1; i <= effectiveTotalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", effectiveTotalPages);
      } else if (currentPage >= effectiveTotalPages - 3) {
        pages.push(
          1,
          "...",
          effectiveTotalPages - 4,
          effectiveTotalPages - 3,
          effectiveTotalPages - 2,
          effectiveTotalPages - 1,
          effectiveTotalPages,
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          effectiveTotalPages,
        );
      }
    }
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  if (loading) {
    return (
      <div
        className="container-fluid p-3 d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-3">
      {/* Error Alert */}
      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Header */}
      <div className="d-flex justify-content-end mb-3">
        {(privileges === "Admin" ||
          privileges === "Manager" ||
          privileges === "Receptionist") && (
          <button
            className="btn btn-sm text-white"
            style={{ backgroundColor: "#2e9290" }}
            onClick={() => setShowAddModal(true)}
          >
            <FaPlus className="me-2" />
            Add Brand
          </button>
        )}
      </div>

      {/* Main Table Card */}
      <div className="card">
        <div className="card-body">
          {/* Controls */}
          <div className="row mb-2">
            <div className="col-md-2">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-table"></i>
                </span>
                <select
                  className="form-select"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setCurrentPage(1);
                    setItemsPerPage(e.target.value);
                  }}
                >
                  <option value="All">Show All</option>
                  {[5, 10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      Show {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-7"></div>
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control pe-5"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <FaSearch
                    className="text-muted"
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ backgroundColor: "#2e9290", color: "white" }}>
                    S.No
                  </th>
                  <th style={{ backgroundColor: "#2e9290", color: "white" }}>
                    Brand Name
                  </th>
                  <th style={{ backgroundColor: "#2e9290", color: "white" }}>
                    Logo
                  </th>
                  <th style={{ backgroundColor: "#2e9290", color: "white" }}>
                    Status
                  </th>
                  <th style={{ backgroundColor: "#2e9290", color: "white" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentBrands.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      {searchTerm
                        ? "No brands found matching your search"
                        : "No brands available"}
                    </td>
                  </tr>
                ) : (
                  currentBrands.map((brand, index) => (
                    <tr key={brand._id} className="align-middle">
                      <td>
                        {itemsPerPage === "All"
                          ? index + 1
                          : (currentPage - 1) * parseInt(itemsPerPage) +
                            index +
                            1}
                      </td>
                      <td>{brand.brand_name}</td>
                      <td>{brand.logo}</td>
                      <td>
                        <span
                          className={`badge rounded-pill fw-semibold px-3 py-2 d-inline-flex align-items-center gap-1 ${
                            brand.status === "Active"
                              ? "bg-success text-white"
                              : "bg-danger text-white"
                          }`}
                        >
                          <FaCheckCircle />
                          {brand.status}
                        </span>
                      </td>
                      <td>
                        {(privileges === "Admin" ||
                          privileges === "Manager") && (
                          <button
                            className="btn btn-sm p-1 border-0"
                            style={{ backgroundColor: "transparent" }}
                            onClick={() => {
                              setEditBrand(brand);
                              setShowEditModal(true);
                            }}
                          >
                            <FaEdit className="text-primary" />
                          </button>
                        )}

                        {privileges === "Admin" && (
                          <button
                            className="btn btn-sm p-1 border-0"
                            style={{ backgroundColor: "transparent" }}
                            onClick={() => handleDeleteBrand(brand._id)}
                          >
                            <FaTrash className="text-danger" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {effectiveTotalPages > 1 && (
            <div className="container">
              <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() => handlePageClick(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="fas fa-chevron-left"></i>
                  <span className="d-none d-sm-inline">Previous</span>
                </button>
                <div
                  className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                  style={{ maxWidth: "100%" }}
                >
                  {pages.map((page, index) => (
                    <button
                      key={index}
                      className={`btn btn-sm px-3 flex-shrink-0 ${
                        page === currentPage
                          ? "text-white"
                          : "btn-outline-secondary"
                      }`}
                      style={
                        page === currentPage
                          ? { backgroundColor: "#2e9290", border: "none" }
                          : {}
                      }
                      onClick={() => handlePageClick(page)}
                      disabled={page === "..."}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() => handlePageClick(currentPage + 1)}
                  disabled={currentPage === effectiveTotalPages}
                >
                  <span className="d-none d-sm-inline">Next</span>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Brand Modal */}
      {showAddModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title">Add Brand</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseAddModal}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleAddBrand}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Brand Name</label>
                      <input
                        type="text"
                        name="brandName"
                        className="form-control"
                        placeholder="e.g., Google"
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Logo (Emoji or URL)</label>
                      <input
                        type="file"
                        name="brandLogo"
                        className="form-control"
                        placeholder="e.g., Mercury"
                      />
                    </div>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">Status</label>
                    <select
                      name="brandStatus"
                      className="form-select"
                      defaultValue="Active"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseAddModal}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{ backgroundColor: "#2e9290", border: "none" }}
                    >
                      Add Brand
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Brand Modal */}
      {showEditModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title">Edit Brand</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseEditModal}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditBrand}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Brand Name</label>
                      <input
                        type="text"
                        name="brandName"
                        className="form-control"
                        defaultValue={editBrand ? editBrand.brand_name : ""}
                        placeholder="e.g., Google"
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Logo</label>
                      {/* Show current logo if available */}
                      {editBrand?.logo && (
                        <div className="mb-2">
                          <img
                            src={editBrand.logo}
                            alt="Current Logo"
                            style={{
                              width: "100px",
                              height: "auto",
                              borderRadius: "8px",
                            }}
                          />
                        </div>
                      )}

                      <input
                        type="file"
                        name="brandLogo"
                        className="form-control"
                        accept="image/*"
                      />
                    </div>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">Status</label>
                    <select
                      name="brandStatus"
                      className="form-select"
                      defaultValue={editBrand ? editBrand.status : "Active"}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseEditModal}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{ backgroundColor: "#2e9290", border: "none" }}
                    >
                      Update Brand
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseDeleteModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete the brand{" "}
                  {brands.find((b) => b._id === deleteBrandId)?.brand_name}?
                </p>
                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseDeleteModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={confirmDelete}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Brand;
